import React, { Fragment, useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { SITENAMEALIAS } from "../utils/init";
import { currentPageAccess } from "../utils/library";
import {
  CreateWebhookEndpoint,
  UpdateWebhookEndpoint,
  GetWebhookEndpoints,
  GetWebhookEvents,
  GetWebhookEndpointById,
  DeleteWebhookEndpoint,
  GetWebhookFilterOptions,
  GetWebhookLogById,
  GetWebhookLogs,
  RetryWebhookLog,
} from "../utils/service";

const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id");

const generateSigningSecret = (merchantId) => {
  const ts = Date.now().toString();
  const raw = String(merchantId) + ts;
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 32; i++) {
    result += chars[(raw.charCodeAt(i % raw.length) + i * 7) % chars.length];
  }
  return "sk_live_" + result;
};

// Helper function to format date as DD-MM-YYYY (for display)
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

// Helper function to format date as DD/MM/YYYY (for API)
const formatDateToAPI = (dateString) => {
  if (!dateString) return "";
  // If already in DD/MM/YYYY format, return as is
  if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateString;
  // If in YYYY-MM-DD format, convert to DD/MM/YYYY
  const [year, month, day] = dateString.split("-");
  if (day && month && year) {
    return `${day}/${month}/${year}`;
  }
  return "";
};

// Helper function to format date as YYYY-MM-DD for input display
const formatDateToYYYYMMDD = (dateString) => {
  if (!dateString) return "";
  // If already in YYYY-MM-DD format, return as is
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return dateString;
  // If in DD-MM-YYYY format, convert
  const [day, month, year] = dateString.split("-");
  if (day && month && year) {
    return `${year}-${month}-${day}`;
  }
  // If in DD/MM/YYYY format, convert
  const parts = dateString.split("/");
  if (parts.length === 3) {
    const [d, m, y] = parts;
    if (d && m && y) {
      return `${y}-${m}-${d}`;
    }
  }
  return "";
};

// Helper to get today's date in YYYY-MM-DD format
const getTodayYYYYMMDD = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to get date 30 days ago (minimum allowed date)
const getMinDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Get 30 days ago as default from date
const getDefaultFromDate = () => {
  return getMinDate();
};

// Helper to get max date (today) for date picker in YYYY-MM-DD format
const getMaxDate = () => {
  return getTodayYYYYMMDD();
};

// Pagination page size constant
const PAGE_SIZE = 20;

const S = {
  page: { padding: "32px 28px 80px" },
  pageHdr: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 28,
    gap: 16,
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-.4px",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSub: {
    fontSize: 13,
    color: "var(--text-color)",
    opacity: 0.5,
    marginTop: 5,
  },
  hdrRight: { display: "flex", gap: 10, alignItems: "center", flexShrink: 0 },

  btn: {
    height: 38,
    padding: "0 18px",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    transition: "all .15s",
    whiteSpace: "nowrap",
    border: "none",
  },
  btnPrimary: {
    background: "var(--btn-color)",
    color: "#fff",
    boxShadow: "0 0 14px rgba(99,102,241,.3)",
  },
  btnInd: {
    background: "transparent",
    border: "1.5px solid var(--btn-color)",
    color: "var(--btn-color)",
    height: 32,
    padding: "0 12px",
    fontSize: 12,
    borderRadius: 7,
    fontFamily: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
  },
  btnGhost: {
    background: "transparent",
    border: "1px solid var(--body-color-less2)",
    color: "var(--text-color)",
    opacity: 0.6,
    height: 32,
    padding: "0 12px",
    fontSize: 12,
    borderRadius: 7,
    fontFamily: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
  },
  btnDanger: {
    background: "transparent",
    border: "1.5px solid rgba(239,68,68,.3)",
    color: "#f87171",
    height: 32,
    padding: "0 12px",
    fontSize: 12,
    borderRadius: 7,
    fontFamily: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
  },
  btnRetry: {
    background: "rgba(245,158,11,.1)",
    border: "1px solid rgba(245,158,11,.3)",
    color: "#f59e0b",
    height: 30,
    padding: "0 12px",
    fontSize: 11,
    borderRadius: 6,
    fontFamily: "inherit",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
  },
  btnCancel: {
    height: 40,
    padding: "0 20px",
    borderRadius: 8,
    border: "1px solid var(--body-color-less2)",
    background: "transparent",
    color: "var(--text-color)",
    opacity: 0.6,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    transition: "all .15s",
  },
  btnSave: {
    height: 40,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    background: "var(--btn-color)",
    color: "#fff",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 0 14px rgba(99,102,241,.3)",
    transition: "filter .15s, transform .15s",
  },
  btnRegen: {
    height: 36,
    padding: "0 14px",
    borderRadius: 7,
    border: "1px solid rgba(245,158,11,.3)",
    background: "rgba(245,158,11,.08)",
    color: "#f59e0b",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  // Pagination styles
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 18px",
    borderTop: "1px solid var(--body-color-less2)",
    flexWrap: "wrap",
    gap: 10,
  },
  paginationInfo: {
    fontSize: 12,
    color: "var(--text-color)",
    opacity: 0.5,
  },
  paginationBtns: {
    display: "flex",
    gap: 5,
    alignItems: "center",
  },
  pageBtn: {
    height: 30,
    width: 30,
    borderRadius: 6,
    border: "1px solid var(--body-color-less2)",
    background: "transparent",
    color: "var(--text-color)",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .15s",
    fontFamily: "inherit",
  },
  pageBtnActive: {
    height: 30,
    width: 30,
    borderRadius: 6,
    border: "1px solid var(--btn-color)",
    background: "var(--btn-color)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .15s",
    fontFamily: "inherit",
  },
  pageBtnDisabled: {
    height: 30,
    width: 30,
    borderRadius: 6,
    border: "1px solid var(--body-color-less2)",
    background: "transparent",
    color: "var(--text-color)",
    fontSize: 12,
    fontWeight: 500,
    cursor: "not-allowed",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.3,
    fontFamily: "inherit",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 9px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
  },
  badgeDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    display: "inline-block",
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(99,102,241,.1)",
    border: "1px solid rgba(99,102,241,.2)",
    color: "var(--btn-color)",
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 20,
  },

  tblWrap: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 12,
    overflow: "hidden",
    transition: "background .3s, border-color .3s",
    marginBottom: 24,
  },
  tblScrollWrap: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  tblHdr: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: "1px solid var(--body-color-less2)",
    flexWrap: "wrap",
    gap: 10,
  },
  tblTitle: { fontSize: 13, fontWeight: 600, color: "var(--text-color)" },
  tblMeta: { fontSize: 12, color: "var(--text-color)", opacity: 0.5 },
  th: {
    padding: "11px 16px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".65px",
    color: "var(--text-color)",
    opacity: 0.5,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: { padding: "13px 16px", fontSize: 13, verticalAlign: "middle" },
  tdUrl: {
    fontFamily: "monospace",
    fontSize: 12,
    color: "var(--btn-color)",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tdActions: { display: "flex", gap: 7, alignItems: "center" },
  trBorder: { borderBottom: "1px solid var(--body-color-less2)" },

  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "56px 20px",
    gap: 13,
  },
  emptyIcon: { fontSize: 36, opacity: 0.35 },
  emptyTitle: { fontSize: 15, fontWeight: 600, color: "var(--text-color)" },
  emptySub: {
    fontSize: 13,
    color: "var(--text-color)",
    opacity: 0.5,
    textAlign: "center",
    maxWidth: 300,
    lineHeight: 1.5,
  },

  drawerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.5)",
    backdropFilter: "blur(3px)",
    zIndex: 100,
  },
  drawer: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: 540,
    maxWidth: "97vw",
    background: "var(--body-color-less)",
    borderLeft: "1px solid var(--body-color-less2)",
    zIndex: 101,
    overflowY: "auto",
    transition: "transform .28s cubic-bezier(.4,0,.2,1)",
    display: "flex",
    flexDirection: "column",
  },
  drawerHdr: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid var(--body-color-less2)",
    position: "sticky",
    top: 0,
    background: "var(--body-color-less)",
    zIndex: 1,
  },
  drawerTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "var(--text-color)",
    letterSpacing: "-.3px",
  },
  drawerClose: {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: "rgba(255,255,255,.05)",
    border: "1px solid var(--body-color-less2)",
    color: "var(--text-color)",
    opacity: 0.5,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 17,
  },
  drawerBody: { padding: "22px 24px", flex: 1 },
  drawerFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "16px 24px",
    borderTop: "1px solid var(--body-color-less2)",
    position: "sticky",
    bottom: 0,
    background: "var(--body-color-less)",
  },
  drawerLoading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 220,
    fontSize: 14,
    color: "var(--text-color)",
    opacity: 0.45,
  },

  field: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-color)",
    opacity: 0.6,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  req: { color: "#ef4444" },
  opt: {
    fontSize: 10,
    fontWeight: 500,
    background: "rgba(100,116,139,.12)",
    color: "var(--text-color)",
    opacity: 0.5,
    borderRadius: 4,
    padding: "1px 6px",
  },
  input: {
    width: "100%",
    height: 40,
    background: "var(--body-color)",
    border: "1.5px solid var(--body-color-less2)",
    borderRadius: 8,
    padding: "0 14px",
    color: "var(--text-color)",
    fontFamily: "inherit",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
  },
  secretWrap: { display: "flex", gap: 9, alignItems: "stretch" },
  formSection: { marginBottom: 22 },
  fsLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".7px",
    color: "var(--text-color)",
    opacity: 0.5,
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: "1px solid var(--body-color-less2)",
  },
  radioOpt: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    cursor: "pointer",
    fontSize: 14,
    color: "var(--text-color)",
    marginBottom: 10,
  },
  checkOpt: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    cursor: "pointer",
    fontSize: 13,
    color: "var(--text-color)",
  },
  eventCode: {
    fontFamily: "monospace",
    fontSize: 12,
    background: "rgba(99,102,241,.08)",
    color: "var(--btn-color)",
    padding: "1px 7px",
    borderRadius: 4,
  },
  eventGroupTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".6px",
    color: "var(--text-color)",
    opacity: 0.5,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  eventList: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },

  summaryCard: {
    background: "var(--body-color)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 10,
    padding: 16,
    marginBottom: 20,
  },
  sumRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "7px 0",
    fontSize: 13,
    borderBottom: "1px solid var(--body-color-less2)",
    gap: 12,
  },
  sumRowLast: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "7px 0",
    fontSize: 13,
    gap: 12,
  },
  sumKey: {
    color: "var(--text-color)",
    opacity: 0.5,
    fontSize: 12,
    flexShrink: 0,
  },
  sumVal: {
    color: "var(--text-color)",
    fontWeight: 500,
    fontFamily: "monospace",
    fontSize: 12,
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "right",
  },

  toggle: {
    width: 38,
    height: 22,
    borderRadius: 11,
    cursor: "pointer",
    position: "relative",
    transition: "background .2s",
    flexShrink: 0,
  },
  toggleThumb: {
    position: "absolute",
    top: 3,
    width: 16,
    height: 16,
    background: "#fff",
    borderRadius: "50%",
    transition: "left .2s",
    boxShadow: "0 1px 3px rgba(0,0,0,.3)",
  },
  inlineField: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 14,
    color: "var(--text-color)",
    marginBottom: 12,
  },
  numInput: {
    width: 80,
    height: 36,
    background: "var(--body-color)",
    border: "1.5px solid var(--body-color-less2)",
    borderRadius: 7,
    padding: "0 10px",
    color: "var(--text-color)",
    fontFamily: "inherit",
    fontSize: 14,
    outline: "none",
    textAlign: "center",
    boxSizing: "border-box",
  },

  viewModalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.6)",
    backdropFilter: "blur(4px)",
    zIndex: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  viewModal: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 14,
    width: "100%",
    maxWidth: 520,
    maxHeight: "88vh",
    overflowY: "auto",
  },
  viewModalHdr: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid var(--body-color-less2)",
    position: "sticky",
    top: 0,
    background: "var(--body-color-less)",
    zIndex: 1,
  },
  viewModalTitle: { fontSize: 15, fontWeight: 700, color: "var(--text-color)" },
  viewModalBody: { padding: "20px 22px" },
  viewSection: { marginBottom: 20 },
  viewSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".6px",
    color: "var(--text-color)",
    opacity: 0.5,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: "1px solid var(--body-color-less2)",
  },
  viewRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "7px 0",
    borderBottom: "1px solid rgba(255,255,255,.04)",
    gap: 12,
  },
  viewKey: {
    fontSize: 12,
    color: "var(--text-color)",
    opacity: 0.5,
    flexShrink: 0,
    minWidth: 110,
  },
  viewVal: {
    fontSize: 12,
    color: "var(--text-color)",
    fontWeight: 500,
    textAlign: "right",
    wordBreak: "break-all",
  },
  viewValMono: {
    fontSize: 12,
    color: "var(--btn-color)",
    fontFamily: "monospace",
    textAlign: "right",
    wordBreak: "break-all",
  },
  viewEventPill: {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(99,102,241,.08)",
    border: "1px solid rgba(99,102,241,.15)",
    color: "var(--btn-color)",
    fontSize: 11,
    fontFamily: "monospace",
    padding: "2px 8px",
    borderRadius: 4,
    margin: "2px 3px 2px 0",
  },
  viewModalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: "14px 22px",
    borderTop: "1px solid var(--body-color-less2)",
  },

  logModalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.65)",
    backdropFilter: "blur(4px)",
    zIndex: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  logModal: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 14,
    width: "100%",
    maxWidth: 580,
    maxHeight: "88vh",
    overflowY: "auto",
  },
  logModalHdr: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: "1px solid var(--body-color-less2)",
    position: "sticky",
    top: 0,
    background: "var(--body-color-less)",
    zIndex: 1,
  },
  logModalTitle: { fontSize: 15, fontWeight: 700, color: "var(--text-color)" },
  logModalBody: { padding: "20px 22px" },
  codeBlock: {
    background: "var(--body-color)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 8,
    padding: 14,
    fontFamily: "monospace",
    fontSize: 12,
    color: "var(--btn-color)",
    whiteSpace: "pre",
    overflowX: "auto",
    lineHeight: 1.6,
    marginTop: 6,
  },
  logSection: { marginBottom: 18 },
  logSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".6px",
    color: "var(--text-color)",
    opacity: 0.5,
    marginBottom: 8,
  },
  respLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 14px",
    background: "var(--body-color)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 7,
    fontFamily: "monospace",
    fontSize: 13,
  },

  confirmBg: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.6)",
    backdropFilter: "blur(3px)",
    zIndex: 250,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmBox: {
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 14,
    padding: 24,
    width: 340,
    maxWidth: "90vw",
  },
  confirmTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text-color)",
    marginBottom: 8,
  },
  confirmBody: {
    fontSize: 13,
    color: "var(--text-color)",
    opacity: 0.5,
    marginBottom: 20,
  },
  confirmBtns: { display: "flex", justifyContent: "flex-end", gap: 10 },

  toast: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 300,
    background: "var(--body-color-less)",
    borderRadius: 10,
    padding: "12px 18px",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text-color)",
    boxShadow: "0 8px 28px rgba(0,0,0,.3)",
    transition: "transform .3s cubic-bezier(.34,1.56,.64,1), opacity .25s",
  },

  select: {
    height: 30,
    padding: "0 26px 0 10px",
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 7,
    color: "var(--text-color)",
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
  },

  dateInput: {
    height: 30,
    padding: "0 10px",
    background: "var(--body-color-less)",
    border: "1px solid var(--body-color-less2)",
    borderRadius: 7,
    color: "var(--text-color)",
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
  },
  dateSeparator: {
    fontSize: 12,
    color: "var(--text-color)",
    opacity: 0.5,
    margin: "0 4px",
  },
  pageBtnActive: {
  height: 30,
  width: 30,
  borderRadius: 6,
  border: "1px solid var(--btn-color)",
  background: "var(--btn-color)",
  color: "#fff",
  fontSize: 14, // Increased from 12
  fontWeight: 700, // Increased from 500
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all .15s",
  fontFamily: "inherit",
  boxShadow: "0 0 0 2px rgba(99,102,241,.2)", // Added subtle glow
},
// Add these new styles to the S object
numInputError: {
  width: 80,
  height: 36,
  background: "var(--body-color)",
  border: "1.5px solid rgba(239,68,68,.4)",
  borderRadius: 7,
  padding: "0 10px",
  color: "var(--text-color)",
  fontFamily: "inherit",
  fontSize: 14,
  outline: "none",
  textAlign: "center",
  boxSizing: "border-box",
},
errorText: {
  color: "#ef4444",
  fontSize: 11,
  marginTop: 4,
  display: "flex",
  alignItems: "center",
  gap: 4,
},
};

const badgeColors = {
  Active: { bg: "rgba(16,185,129,.1)", color: "#10b981" },
  Disabled: { bg: "rgba(100,116,139,.1)", color: "var(--text-color)" },
  Failed: { bg: "rgba(239,68,68,.1)", color: "#f87171" },
};

function Badge({ status }) {
  const c = badgeColors[status] || badgeColors.Disabled;
  return (
    <span style={{ ...S.badge, background: c.bg, color: c.color }}>
      <span style={{ ...S.badgeDot, background: c.color }} />
      {status}
    </span>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <div
      style={{
        ...S.toggle,
        background: checked ? "var(--btn-color)" : "rgba(100,116,139,.25)",
      }}
      onClick={() => onChange(!checked)}
    >
      <div style={{ ...S.toggleThumb, left: checked ? 19 : 3 }} />
    </div>
  );
}

// Pagination Component
// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(0, currentPage - 2);
      let end = Math.min(totalPages - 1, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(0, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div style={S.pagination}>
      <div style={S.paginationInfo}>
        Page {currentPage + 1} of {totalPages}
      </div>
      <div style={S.paginationBtns}>
        <button
          style={currentPage === 0 ? S.pageBtnDisabled : S.pageBtn}
          onClick={() => onPageChange(0)}
          disabled={currentPage === 0}
          title="First Page"
        >
          <i className="mdi mdi-chevron-double-left" style={{ fontSize: 14 }} />
        </button>
        <button
          style={currentPage === 0 ? S.pageBtnDisabled : S.pageBtn}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 0}
          title="Previous Page"
        >
          <i className="mdi mdi-chevron-left" style={{ fontSize: 14 }} />
        </button>
        
        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            style={
              pageNum === currentPage 
                ? {
                    ...S.pageBtnActive,
                    // Enhanced active state styling
                    background: "var(--btn-color)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    boxShadow: "0 0 0 3px rgba(99,102,241,.3), 0 2px 8px rgba(99,102,241,.4)",
                    transform: "scale(1.1)",
                    border: "2px solid var(--btn-color)",
                    position: "relative",
                    zIndex: 1,
                  }
                : S.pageBtn
            }
            onClick={() => onPageChange(pageNum)}
            aria-current={pageNum === currentPage ? "page" : undefined}
          >
            {pageNum + 1}
          </button>
        ))}
        
        <button
          style={currentPage >= Math.max(0, totalPages - 1) ? S.pageBtnDisabled : S.pageBtn}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= Math.max(0, totalPages - 1)}
          title="Next Page"
        >
          <i className="mdi mdi-chevron-right" style={{ fontSize: 14 }} />
        </button>
        <button
          style={currentPage >= Math.max(0, totalPages - 1) ? S.pageBtnDisabled : S.pageBtn}
          onClick={() => onPageChange(Math.max(0, totalPages - 1))}
          disabled={currentPage >= Math.max(0, totalPages - 1)}
          title="Last Page"
        >
          <i className="mdi mdi-chevron-double-right" style={{ fontSize: 14 }} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───
const Webhooks = () => {
  const isReadOnly = currentPageAccess() === "READ";

  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const [allEvents, setAllEvents] = useState([]);
  const [eventGroups, setEventGroups] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Drawer (add / edit)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingEpData, setEditingEpData] = useState(null);

  // Form fields
  const [epUrl, setEpUrl] = useState("");
  const [epDesc, setEpDesc] = useState("");
  const [eventsMode, setEventsMode] = useState("all");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [epSecret, setEpSecret] = useState("");
  const [epRetry, setEpRetry] = useState(true);
  const [epMaxRetries, setEpMaxRetries] = useState(5);
  const [epTimeout, setEpTimeout] = useState(5);
  const [epStatus, setEpStatus] = useState("Active");
  const [saving, setSaving] = useState(false);

  // View modal (read-only)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalData, setViewModalData] = useState(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);

  // Log modal
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logModalLoading, setLogModalLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Log filter
  const [logFilter, setLogFilter] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);

  // Date filter for logs - store in YYYY-MM-DD for input display
  const [logFromDate, setLogFromDate] = useState(getDefaultFromDate());
  const [logToDate, setLogToDate] = useState(getTodayYYYYMMDD());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmBody, setConfirmBody] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const confirmCb = useRef(null);

  // Toast
  const [toastMsg, setToastMsg] = useState("");
  const [toastShow, setToastShow] = useState(false);
  const [toastType, setToastType] = useState("success");
  const toastTimer = useRef(null);

  const [retryLoadingId, setRetryLoadingId] = useState(null);

  // Ref to preserve selected events when switching modes
  const savedSelectedEventsRef = useRef([]);

  useEffect(() => {
    fetchEndpoints();
    fetchEvents();
    fetchFilterOptions();
    fetchLogs();
  }, []);

  // Fetch logs when date filters change
  useEffect(() => {
    if (logFromDate && logToDate) {
      setCurrentPage(0);
      fetchLogs(0);
    }
  }, [logFromDate, logToDate]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        closeDrawer();
        setViewModalOpen(false);
        setLogModalOpen(false);
        setConfirmOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Handle events mode switching to preserve selections
  useEffect(() => {
    if (eventsMode === "all") {
      savedSelectedEventsRef.current = selectedEvents;
      setSelectedEvents([...allEvents]);
    } else if (eventsMode === "select") {
      if (savedSelectedEventsRef.current.length > 0) {
        setSelectedEvents(savedSelectedEventsRef.current);
      } else if (editingEpData?.events && editingEpData.events.length > 0) {
        setSelectedEvents(editingEpData.events);
      } else {
        setSelectedEvents([...allEvents]);
      }
    }
  }, [eventsMode]);

  // ── API calls ──
  const fetchEndpoints = async () => {
    setLoading(true);
    try {
      const res = await GetWebhookEndpoints(getMerchantId());
      if (res.data && res.data.error === "0") {
        const mapped = (res.data.data || []).map((ep) => ({
          id: ep.epId,
          url: ep.url || "",
          desc: ep.description || "",
          eventsMode: ep.eventsMode || "all",
          events: ep.events || [],
          secret: ep.secret || "",
          retry: ep.retryEnabled === "Y",
          maxRetries: ep.maxRetries || 0,
          timeout: ep.timeoutSec || 10,
          status: ep.status || "Active",
          lastDelivery: ep.lastDelivery || "—",
          eventCount: ep.eventCount || 0,
        }));
        setEndpoints(mapped);
      }
    } catch (err) {
      console.error("Failed to fetch webhook endpoints", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    setEventsLoading(true);
    try {
      const res = await GetWebhookEvents();
      if (res.data && res.data.error === "0") {
        const events = res.data.data || [];
        const names = events
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((e) => e.eventName);
        setAllEvents(names);
        setSelectedEvents(names);

        const groupMap = {};
        events.forEach((e) => {
          if (!groupMap[e.eventGroup]) groupMap[e.eventGroup] = [];
          groupMap[e.eventGroup].push(e);
        });
        const groups = Object.entries(groupMap).map(([label, items]) => ({
          label,
          events: items
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((i) => i.eventName),
        }));
        setEventGroups(groups);
      }
    } catch (err) {
      console.error("Failed to fetch webhook events", err);
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await GetWebhookFilterOptions(getMerchantId());
      if (res.data && res.data.error === "0") {
        setFilterOptions(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch filter options", err);
    }
  };

  const fetchLogs = async (page = currentPage) => {
    setLogsLoading(true);
    try {
      const merchantId = getMerchantId();
      const fromDateParam = logFromDate ? formatDateToAPI(logFromDate) : null;
      const toDateParam = logToDate ? formatDateToAPI(logToDate) : null;

      console.log("Fetching logs:", {
        fromDate: fromDateParam,
        toDate: toDateParam,
        page: page,
        size: PAGE_SIZE,
      });

      const res = await GetWebhookLogs(
        merchantId,
        fromDateParam,
        toDateParam,
        page,
        PAGE_SIZE
      );

      if (res.data && res.data.error === "0") {
        setLogs(res.data.data || []);
        
        if (res.data.totalRecords !== undefined) {
          const calculatedTotalPages = Math.ceil(res.data.totalRecords / PAGE_SIZE);
          setTotalPages(calculatedTotalPages);
          setCurrentPage(res.data.page || page);
        } else {
          const hasMore = (res.data.data || []).length === PAGE_SIZE;
          setTotalPages(hasMore ? page + 2 : page + 1);
        }
      }
    } catch (err) {
      console.error("Failed to fetch delivery logs", err);
    } finally {
      setLogsLoading(false);
    }
  };

  // ── Helpers ──
  const showToast = (msg, type = "success") => {
    setToastMsg(msg);
    setToastType(type);
    setToastShow(true);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShow(false), 3000);
  };

  const openConfirm = (title, body, cb) => {
    setConfirmTitle(title);
    setConfirmBody(body);
    confirmCb.current = cb;
    setConfirmLoading(false);
    setConfirmOpen(true);
  };

  // ── View modal (read-only) ──
  const openViewModal = async (id) => {
    setViewModalOpen(true);
    setViewModalData(null);
    setViewModalLoading(true);
    try {
      const res = await GetWebhookEndpointById(id);
      if (res.data && res.data.error === "0") {
        setViewModalData(res.data.data);
      } else {
        showToast("Failed to load endpoint details", "error");
        setViewModalOpen(false);
      }
    } catch (err) {
      console.error("Failed to fetch endpoint details", err);
      showToast("Failed to load endpoint details", "error");
      setViewModalOpen(false);
    } finally {
      setViewModalLoading(false);
    }
  };

  // ── Drawer (add / edit) ──
  const openDrawer = async (id) => {
    setEditingId(id);
    setDrawerOpen(true);

    if (id) {
      setDrawerLoading(true);
      setEditingEpData(null);
      try {
        const res = await GetWebhookEndpointById(id);
        if (res.data && res.data.error === "0") {
          const ep = res.data.data;
          setEditingEpData(ep);
          setEpUrl(ep.url || "");
          setEpDesc(ep.description || "");
          setEventsMode(ep.eventsMode || "all");

          if (
            ep.eventsMode === "select" &&
            ep.events &&
            Array.isArray(ep.events)
          ) {
            setSelectedEvents(ep.events);
            savedSelectedEventsRef.current = ep.events;
          } else {
            setSelectedEvents([...allEvents]);
            savedSelectedEventsRef.current = [...allEvents];
          }

          setEpSecret(ep.secret || "");
          setEpRetry(ep.retryEnabled === "Y");
          setEpMaxRetries(ep.maxRetries || 5);
          setEpTimeout(ep.timeoutSec || 5);
          setEpStatus(
            ep.status === "Failed" ? "Active" : ep.status || "Active"
          );
        }
      } catch (err) {
        console.error("Failed to fetch endpoint details", err);
        showToast("Failed to load endpoint details", "error");
        setDrawerOpen(false);
      } finally {
        setDrawerLoading(false);
      }
    } else {
      setEditingEpData(null);
      setEpUrl("");
      setEpDesc("");
      setEventsMode("all");
      setSelectedEvents([...allEvents]);
      savedSelectedEventsRef.current = [...allEvents];
      setEpSecret(generateSigningSecret(getMerchantId()));
      setEpRetry(true);
      setEpMaxRetries(5);
      setEpTimeout(5);
      setEpStatus("Active");
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setEditingEpData(null);
  };

  const toggleEvent = (ev) => {
    setSelectedEvents((prev) =>
      prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]
    );
  };

  // ── Save endpoint (create OR update) ──
  const saveEndpoint = async () => {
      if (!epUrl.trim()) {
    showToast("Endpoint URL is required", "error");
    return;
  }

  // Add validation for max retries and timeout
  if (epMaxRetries > 5) {
    showToast("Max retries cannot exceed 5", "error");
    setEpMaxRetries(5);
    return;
  }

  if (epTimeout > 5) {
    showToast("Timeout cannot exceed 5 seconds", "error");
    setEpTimeout(5);
    return;
  }

  if (epTimeout < 1) {
    showToast("Timeout must be at least 1 second", "error");
    setEpTimeout(0);
    return;
  }

  setSaving(true);

    const merchantId = getMerchantId();
    const endpointPayload = {
      endpoint: {
        url: epUrl.trim(),
        description: epDesc.trim(),
        eventsMode: eventsMode,
        secret: epSecret,
        retryEnabled: epRetry ? "Y" : "N",
        maxRetries: epMaxRetries,
        timeoutSec: epTimeout,
        status: epStatus,
      },
      events: eventsMode === "all" ? allEvents : selectedEvents,
      merchantId: merchantId,
    };

    try {
      if (editingId) {
        const res = await UpdateWebhookEndpoint(editingId, endpointPayload);
        if (res.data && res.data.error === "0") {
          showToast("Endpoint updated successfully");
          closeDrawer();
          fetchEndpoints();
        } else {
          showToast(res.data?.message || "Failed to update endpoint", "error");
        }
      } else {
        const res = await CreateWebhookEndpoint(endpointPayload);
        if (res.data && res.data.error === "0") {
          showToast("Endpoint added successfully");
          closeDrawer();
          fetchEndpoints();
        } else {
          showToast(res.data?.message || "Failed to create endpoint", "error");
        }
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (editingId ? "Failed to update endpoint" : "Failed to create endpoint");
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteEndpoint = (id) => {
    openConfirm(
      "Delete Endpoint?",
      "This endpoint and all its delivery history will be permanently removed.",
      async () => {
        setConfirmLoading(true);
        try {
          const res = await DeleteWebhookEndpoint(id);
          if (res.data && res.data.error === "0") {
            setEndpoints((prev) => prev.filter((e) => e.id !== id));
            showToast("Endpoint deleted");
          } else {
            showToast(
              res.data?.message || "Failed to delete endpoint",
              "error"
            );
          }
        } catch (err) {
          showToast(
            err?.response?.data?.message || "Failed to delete endpoint",
            "error"
          );
        } finally {
          setConfirmLoading(false);
          setConfirmOpen(false);
        }
      }
    );
  };

  const regenerateSecret = () => {
    setEpSecret(generateSigningSecret(getMerchantId()));
    showToast("Signing secret regenerated — save the endpoint to apply");
  };

  const viewLog = async (logId) => {
    setSelectedLog(null);
    setLogModalLoading(true);
    setLogModalOpen(true);
    try {
      const res = await GetWebhookLogById(logId);
      if (res.data && res.data.error === "0") {
        setSelectedLog(res.data.data);
      } else {
        showToast("Failed to load log details", "error");
        setLogModalOpen(false);
      }
    } catch (err) {
      console.error("Failed to fetch log details", err);
      showToast("Failed to load log details", "error");
      setLogModalOpen(false);
    } finally {
      setLogModalLoading(false);
    }
  };

  const retryLog = async (logId) => {
    setRetryLoadingId(logId);
    try {
      const res = await RetryWebhookLog(logId);

      if (res.data && res.data.error === "0") {
        showToast("Retry triggered successfully");
        fetchLogs();
      } else {
        showToast(res.data?.message || "Retry failed", "error");
      }
    } catch (err) {
      showToast(err?.response?.data?.message || "Retry failed", "error");
    } finally {
      setRetryLoadingId(null);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchLogs(newPage);
  };

  const filteredLogs = () => {
    let result = logFilter
      ? logs.filter((l) => l.epId === parseInt(logFilter))
      : logs;
    return result;
  };

  const handleFromDateChange = (e) => {
    const selectedDate = e.target.value;
    const minDate = getMinDate();
    const maxDate = getMaxDate();
    
    if (selectedDate >= minDate && selectedDate <= maxDate) {
      // Check if range exceeds 30 days relative to current toDate
      const fromObj = new Date(selectedDate);
      const toObj = new Date(logToDate);
      const diffDays = Math.ceil((toObj - fromObj) / (1000 * 60 * 60 * 24));
      
      if (diffDays > 30) {
        showToast("Date range cannot exceed 30 days", "error");
        return;
      }

      setLogFromDate(selectedDate);
      if (selectedDate > logToDate) {
        setLogToDate(selectedDate);
      }
    } else {
      showToast(`From date must be between ${formatDateToDDMMYYYY(minDate)} and ${formatDateToDDMMYYYY(maxDate)}`, "error");
    }
  };

  const handleToDateChange = (e) => {
    const selectedDate = e.target.value;
    const minDate = logFromDate;
    const maxDate = getMaxDate();
    
    if (selectedDate >= minDate && selectedDate <= maxDate) {
      // Check if range exceeds 30 days relative to current fromDate
      const fromObj = new Date(logFromDate);
      const toObj = new Date(selectedDate);
      const diffDays = Math.ceil((toObj - fromObj) / (1000 * 60 * 60 * 24));
      
      if (diffDays > 30) {
        showToast("Date range cannot exceed 30 days", "error");
        return;
      }

      setLogToDate(selectedDate);
    } else {
      showToast(`To date must be between ${formatDateToDDMMYYYY(minDate)} and ${formatDateToDDMMYYYY(maxDate)}`, "error");
    }
  };

  return (
    <Fragment>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={S.page}>
            {/* ── Page Header ── */}
            <div style={S.pageHdr}>
              <div>
                <h1 style={S.pageTitle}>Webhooks</h1>
                <p style={S.pageSub}>
                  Receive real-time payment and subscription events
                </p>
              </div>
              <div style={S.hdrRight}>
                {!isReadOnly && (
                  <button
                    style={{ ...S.btn, ...S.btnPrimary }}
                    onClick={() => openDrawer(null)}
                  >
                    <i className="mdi mdi-plus" style={{ fontSize: 15 }} />
                    Add Endpoint
                  </button>
                )}
              </div>
            </div>

            {/* ── Endpoints Table ── */}
            <div style={S.tblWrap}>
              <div style={S.tblHdr}>
                <span style={S.tblTitle}>Endpoints</span>
                <span style={S.tblMeta}>
                  {endpoints.length} endpoint{endpoints.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div style={S.tblScrollWrap}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 640,
                  }}
                >
                  <thead>
                    <tr style={S.trBorder}>
                      <th style={S.th}>Endpoint URL</th>
                      <th style={S.th}>Events</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Last Delivery</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5}>
                          <div style={{ ...S.empty, padding: "40px 20px" }}>
                            <div
                              style={{
                                fontSize: 14,
                                color: "var(--text-color)",
                                opacity: 0.5,
                              }}
                            >
                              Loading endpoints...
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : endpoints.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <div style={S.empty}>
                            <div style={S.emptyIcon}>
                              <i className="mdi mdi-webhook" />
                            </div>
                            <div style={S.emptyTitle}>
                              No webhook endpoints yet
                            </div>
                            <div style={S.emptySub}>
                              Receive real-time updates when payments,
                              subscriptions, and checkouts happen
                            </div>
                            {!isReadOnly && (
                              <button
                                style={{
                                  ...S.btn,
                                  ...S.btnPrimary,
                                  marginTop: 4,
                                }}
                                onClick={() => openDrawer(null)}
                              >
                                Add Endpoint
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      endpoints.map((ep) => (
                        <tr key={ep.id} style={S.trBorder}>
                          <td style={S.td}>
                            <div style={S.tdUrl} title={ep.url}>
                              {ep.url}
                            </div>
                            {ep.desc && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "var(--text-color)",
                                  opacity: 0.5,
                                  marginTop: 2,
                                }}
                              >
                                {ep.desc}
                              </div>
                            )}
                          </td>
                          <td style={S.td}>
                            <span style={S.chip}>
                              {ep.eventsMode === "all"
                                ? "All events"
                                : `${
                                    ep.events.length || ep.eventCount || 0
                                  } events`}
                            </span>
                          </td>
                          <td style={S.td}>
                            <Badge status={ep.status} />
                          </td>
                          <td
                            style={{
                              ...S.td,
                              fontSize: 12,
                              color: "var(--text-color)",
                              opacity: 0.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ep.lastDelivery}
                          </td>
                          <td style={S.td}>
                            <div style={S.tdActions}>
                              <button
                                style={{
                                  ...S.btnGhost,
                                  ...(isReadOnly
                                    ? { opacity: 0.5, cursor: "not-allowed" }
                                    : null),
                                }}
                                disabled={isReadOnly}
                                onClick={() => openViewModal(ep.id)}
                              >
                                View
                              </button>
                              <button
                                style={{
                                  ...S.btnInd,
                                  ...(isReadOnly
                                    ? { opacity: 0.5, cursor: "not-allowed" }
                                    : null),
                                }}
                                disabled={isReadOnly}
                                onClick={() => openDrawer(ep.id)}
                              >
                                Edit
                              </button>
                              <button
                                style={{
                                  ...S.btnDanger,
                                  ...(isReadOnly
                                    ? { opacity: 0.5, cursor: "not-allowed" }
                                    : null),
                                }}
                                disabled={isReadOnly}
                                onClick={() => deleteEndpoint(ep.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Delivery Logs Table ── */}
            <div style={S.tblWrap}>
              <div style={S.tblHdr}>
                <span style={S.tblTitle}>Delivery Logs</span>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5 }}>
                      From
                    </span>
                    <input
                      type="date"
                      value={logFromDate}
                      onChange={handleFromDateChange}
                      min={getMinDate()}
                      max={getMaxDate()}
                      style={S.dateInput}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5 }}>
                      To
                    </span>
                    <input
                      type="date"
                      value={logToDate}
                      onChange={handleToDateChange}
                      min={logFromDate || getMinDate()}
                      max={getMaxDate()}
                      style={S.dateInput}
                    />
                  </div>
                  <select
                    style={S.select}
                    value={logFilter}
                    onChange={(e) => {
                      setLogFilter(e.target.value);
                      setCurrentPage(0);
                      fetchLogs(0);
                    }}
                  >
                    <option value="">All endpoints</option>
                    {filterOptions.map((opt) => (
                      <option key={opt.epId} value={opt.epId}>
                        {opt.url.replace("https://", "")}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={S.tblScrollWrap}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: 620,
                  }}
                >
                  <thead>
                    <tr style={S.trBorder}>
                      <th style={S.th}>Event Type</th>
                      <th style={S.th}>Endpoint</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Response</th>
                      <th style={S.th}>Time</th>
                      <th style={S.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading ? (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ ...S.empty, padding: "40px 20px" }}>
                            <div
                              style={{
                                fontSize: 14,
                                color: "var(--text-color)",
                                opacity: 0.5,
                              }}
                            >
                              Loading delivery logs...
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredLogs().length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ ...S.empty, padding: "32px 20px" }}>
                            <div style={S.emptyIcon}>
                              <i className="mdi mdi-text-box-search-outline" />
                            </div>
                            <div style={S.emptyTitle}>No delivery logs</div>
                            <div style={S.emptySub}>
                              {logFromDate && logToDate
                                ? `No logs found from ${formatDateToDDMMYYYY(
                                    logFromDate
                                  )} to ${formatDateToDDMMYYYY(logToDate)}`
                                : "No logs found"}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredLogs().map((l) => (
                        <tr key={l.logId} style={S.trBorder}>
                          <td style={S.td}>
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 12,
                                color: "var(--btn-color)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {l.eventName}
                            </span>
                          </td>
                          <td
                            style={{ ...S.td, ...S.tdUrl, maxWidth: 160 }}
                            title={l.epUrl}
                          >
                            {l.epUrl}
                          </td>
                          <td style={S.td}>
                            <span
                              style={{
                                ...S.badge,
                                background:
                                  l.status === "SUCCESS"
                                    ? "rgba(16,185,129,.08)"
                                    : "rgba(239,68,68,.08)",
                                color:
                                  l.status === "SUCCESS"
                                    ? "#10b981"
                                    : "#f87171",
                                fontSize: 11,
                                padding: "2px 8px",
                              }}
                            >
                              <span
                                style={{
                                  ...S.badgeDot,
                                  background:
                                    l.status === "SUCCESS"
                                      ? "#10b981"
                                      : "#f87171",
                                }}
                              />
                              {l.status === "SUCCESS" ? "Success" : "Failed"}
                            </span>
                          </td>
                          <td
                            style={{
                              ...S.td,
                              fontFamily: "monospace",
                              fontSize: 12,
                              color: l.respCode < 400 ? "#10b981" : "#ef4444",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {l.respCode}
                          </td>
                          <td
                            style={{
                              ...S.td,
                              fontSize: 12,
                              color: "var(--text-color)",
                              opacity: 0.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {l.timeDisplay}
                          </td>
                          <td style={S.td}>
                            <div style={{ display: "flex", gap: 7 }}>
                              <button
                                style={S.btnGhost}
                                onClick={() => viewLog(l.logId)}
                              >
                                View
                              </button>
                              {l.status === "FAILED" && (
                                <button
                                  style={S.btnRetry}
                                  onClick={() => retryLog(l.logId)}
                                  disabled={retryLoadingId === l.logId}
                                >
                                  {retryLoadingId === l.logId
                                    ? "Retrying..."
                                    : "Retry"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* ── Pagination ── */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* ── Drawer Overlay ── */}
      {drawerOpen && (
        <div
          style={{ ...S.drawerOverlay, opacity: 1, pointerEvents: "all" }}
          onClick={closeDrawer}
        />
      )}

      {/* ── Drawer ── */}
      <div
        style={{
          ...S.drawer,
          transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div style={S.drawerHdr}>
          <span style={S.drawerTitle}>
            {editingId ? "Edit Endpoint" : "Add Endpoint"}
          </span>
          <button style={S.drawerClose} onClick={closeDrawer}>
            &times;
          </button>
        </div>

        <div style={S.drawerBody}>
          {drawerLoading ? (
            <div style={S.drawerLoading}>Loading endpoint details...</div>
          ) : (
            <>
              {/* Summary card — edit mode */}
              {editingId && editingEpData && (
                <div style={S.summaryCard}>
                  <div style={S.sumRow}>
                    <span style={S.sumKey}>Endpoint URL</span>
                    <span style={S.sumVal}>{editingEpData.url}</span>
                  </div>
                  <div style={S.sumRow}>
                    <span style={S.sumKey}>Events</span>
                    <span style={S.sumVal}>
                      {editingEpData.eventsMode === "all"
                        ? "All events"
                        : `${editingEpData.eventCount || 0} selected`}
                    </span>
                  </div>
                  <div style={S.sumRow}>
                    <span style={S.sumKey}>Status</span>
                    <Badge status={editingEpData.status} />
                  </div>
                  <div style={S.sumRowLast}>
                    <span style={S.sumKey}>Last Delivery</span>
                    <span style={S.sumVal}>
                      {editingEpData.lastDelivery || "—"}
                    </span>
                  </div>
                </div>
              )}

              {/* Basic Info */}
              <div style={S.formSection}>
                <div style={S.fsLabel}>Basic Info</div>
                <div style={S.field}>
                  <label style={S.label}>
                    Endpoint URL <span style={S.req}>*</span>
                  </label>
                  <input
                    style={S.input}
                    type="url"
                    value={epUrl}
                    onChange={(e) => setEpUrl(e.target.value)}
                    placeholder="https://yourdomain.com/webhook"
                  />
                </div>
                <div style={S.field}>
                  <label style={S.label}>
                    Description <span style={S.opt}>optional</span>
                  </label>
                  <input
                    style={S.input}
                    value={epDesc}
                    onChange={(e) => setEpDesc(e.target.value)}
                    placeholder="e.g. Internal notifications"
                  />
                </div>
              </div>

              {/* Events */}
              <div style={S.formSection}>
                <div style={S.fsLabel}>Events to Send</div>
                <label style={S.radioOpt}>
                  <input
                    type="radio"
                    name="eventsMode"
                    checked={eventsMode === "all"}
                    onChange={() => setEventsMode("all")}
                    style={{
                      accentColor: "var(--btn-color)",
                      width: 15,
                      height: 15,
                    }}
                  />
                  All events
                </label>
                <label style={S.radioOpt}>
                  <input
                    type="radio"
                    name="eventsMode"
                    checked={eventsMode === "select"}
                    onChange={() => setEventsMode("select")}
                    style={{
                      accentColor: "var(--btn-color)",
                      width: 15,
                      height: 15,
                    }}
                  />
                  Select events
                </label>
                {eventsMode === "select" && (
                  <div
                    style={{
                      background: "var(--body-color)",
                      border: "1px solid var(--body-color-less2)",
                      borderRadius: 9,
                      padding: 16,
                      marginTop: 12,
                    }}
                  >
                    {eventsLoading ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          opacity: 0.5,
                          textAlign: "center",
                          padding: "20px 0",
                        }}
                      >
                        Loading events...
                      </div>
                    ) : eventGroups.length === 0 ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--text-color)",
                          opacity: 0.5,
                          textAlign: "center",
                          padding: "20px 0",
                        }}
                      >
                        No events available
                      </div>
                    ) : (
                      eventGroups.map((group) => (
                        <div key={group.label} style={{ marginBottom: 16 }}>
                          <div style={S.eventGroupTitle}>
                            {group.label}
                            <span
                              style={{
                                flex: 1,
                                height: 1,
                                background: "var(--body-color-less2)",
                                display: "inline-block",
                              }}
                            />
                          </div>
                          <div style={S.eventList}>
                            {group.events.map((ev) => (
                              <label key={ev} style={S.checkOpt}>
                                <input
                                  type="checkbox"
                                  checked={selectedEvents.includes(ev)}
                                  onChange={() => toggleEvent(ev)}
                                  style={{
                                    accentColor: "var(--btn-color)",
                                    width: 14,
                                    height: 14,
                                  }}
                                />
                                <span style={S.eventCode}>{ev}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Security */}
              <div style={S.formSection}>
                <div style={S.fsLabel}>Security</div>
                <div style={S.field}>
                  <label style={S.label}>Signing Secret</label>
                  <div style={S.secretWrap}>
                    <input
                      style={{
                        ...S.input,
                        flex: 1,
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: "var(--text-color)",
                        opacity: 0.6,
                        cursor: "default",
                      }}
                      value={epSecret}
                      readOnly
                    />
                    <button style={S.btnRegen} onClick={regenerateSecret}>
                      <i className="mdi mdi-refresh" style={{ fontSize: 14 }} />{" "}
                      Regenerate
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-color)",
                      opacity: 0.45,
                      marginTop: 5,
                    }}
                  >
                    Used to verify the authenticity of webhook payloads
                  </div>
                </div>
              </div>

              {/* Delivery Settings */}
<div style={S.formSection}>
  <div style={S.fsLabel}>Delivery Settings</div>
  <div style={S.inlineField}>
    <Toggle checked={epRetry} onChange={setEpRetry} />
    <span
      style={{
        fontSize: 13,
        fontWeight: 500,
        color: "var(--text-color)",
      }}
    >
      Retry on failure
    </span>
  </div>
  <div style={S.inlineField}>
    <span
      style={{
        fontSize: 13,
        color: "var(--text-color)",
        opacity: 0.5,
        minWidth: 100,
      }}
    >
      Max retries
    </span>
    <input
      style={epMaxRetries > 5 ? S.numInputError : S.numInput}
      type="number"
      min={0}
      max={5}
      value={epMaxRetries}
      onChange={(e) => {
        const inputValue = e.target.value;
        
        // Allow empty string while typing
        if (inputValue === '' || inputValue === '-') {
          setEpMaxRetries('');
          return;
        }
        
        let value = parseInt(inputValue);
        
        // Handle invalid numbers
        if (isNaN(value)) {
          setEpMaxRetries(0);
          return;
        }
        
        // Enforce limits
        if (value > 5) {
          value = 5;
          showToast("Max retries cannot exceed 5", "error");
        }
        if (value < 0) {
          value = 0;
          showToast("Max retries cannot be negative", "error");
        }
        
        setEpMaxRetries(value);
      }}
      onBlur={() => {
        // Ensure minimum value when field loses focus
        if (epMaxRetries === '' || epMaxRetries < 0) {
          setEpMaxRetries(0);
        }
      }}
      onKeyDown={(e) => {
        // Allow: backspace, delete, tab, escape, enter, arrow keys
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (allowedKeys.includes(e.key)) {
          return;
        }
        // Prevent non-numeric characters
        if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
          e.preventDefault();
        }
        // Only allow numbers 0-5
        if (/^[0-9]$/.test(e.key)) {
          const currentValue = e.target.value;
          const newValue = currentValue + e.key;
          if (parseInt(newValue) > 5) {
            e.preventDefault();
            showToast("Max retries cannot exceed 5", "error");
          }
        }
      }}
    />
    {epMaxRetries > 5 && (
      <span style={S.errorText}>
        <i className="mdi mdi-alert-circle" style={{ fontSize: 12 }} />
        Max retries cannot exceed 5
      </span>
    )}
  </div>
  <div style={S.inlineField}>
    <span
      style={{
        fontSize: 13,
        color: "var(--text-color)",
        opacity: 0.5,
        minWidth: 100,
      }}
    >
      Timeout
    </span>
    <input
      style={epTimeout > 5 ? S.numInputError : S.numInput}
      type="number"
      min={1}
      max={5}
      value={epTimeout}
      onChange={(e) => {
        const inputValue = e.target.value;
        
        // Allow empty string while typing
        if (inputValue === '' || inputValue === '-') {
          setEpTimeout('');
          return;
        }
        
        let value = parseInt(inputValue);
        
        // Handle invalid numbers
        if (isNaN(value)) {
          setEpTimeout(1);
          return;
        }
        
        // Enforce limits
        if (value > 5) {
          value = 5;
          showToast("Timeout cannot exceed 5 seconds", "error");
        }
        if (value < 1) {
          value = 1;
          showToast("Timeout must be at least 1 second", "error");
        }
        
        setEpTimeout(value);
      }}
      onBlur={() => {
        // Ensure minimum value when field loses focus
        if (epTimeout === '' || epTimeout < 1) {
          setEpTimeout(1);
        }
      }}
      onKeyDown={(e) => {
        // Allow: backspace, delete, tab, escape, enter, arrow keys
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (allowedKeys.includes(e.key)) {
          return;
        }
        // Prevent non-numeric characters
        if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-' || e.key === '.') {
          e.preventDefault();
        }
        // Only allow numbers 1-5
        if (/^[0-9]$/.test(e.key)) {
          const currentValue = e.target.value;
          const newValue = currentValue + e.key;
          if (parseInt(newValue) > 5) {
            e.preventDefault();
            showToast("Timeout cannot exceed 5 seconds", "error");
          }
        }
      }}
    />
    <span
      style={{
        fontSize: 13,
        color: "var(--text-color)",
        opacity: 0.5,
      }}
    >
      seconds
    </span>
    {epTimeout > 5 && (
      <span style={S.errorText}>
        <i className="mdi mdi-alert-circle" style={{ fontSize: 12 }} />
        Timeout cannot exceed 5 seconds
      </span>
    )}
  </div>
</div>

              {/* Status */}
              <div style={{ ...S.formSection, marginBottom: 0 }}>
                <div style={S.fsLabel}>Status</div>
                <label style={S.radioOpt}>
                  <input
                    type="radio"
                    name="epStatus"
                    checked={epStatus === "Active"}
                    onChange={() => setEpStatus("Active")}
                    style={{
                      accentColor: "var(--btn-color)",
                      width: 15,

                      height: 15,
                    }}
                  />
                  Active
                </label>
                <label style={S.radioOpt}>
                  <input
                    type="radio"
                    name="epStatus"
                    checked={epStatus === "Disabled"}
                    onChange={() => setEpStatus("Disabled")}
                    style={{
                      accentColor: "var(--btn-color)",
                      width: 15,
                      height: 15,
                    }}
                  />
                  Disabled
                </label>
              </div>
            </>
          )}
        </div>

        <div style={S.drawerFooter}>
          <button style={S.btnCancel} onClick={closeDrawer}>
            Cancel
          </button>
          <button
            style={{ ...S.btnSave, opacity: saving || drawerLoading ? 0.6 : 1 }}
            onClick={saveEndpoint}
            disabled={saving || drawerLoading}
          >
            {saving
              ? editingId
                ? "Updating..."
                : "Saving..."
              : editingId
              ? "Update Endpoint"
              : "Save Endpoint"}
          </button>
        </div>
      </div>

      {/* ── View Modal (read-only) ── */}
      {viewModalOpen && (
        <div
          style={{ ...S.viewModalOverlay, pointerEvents: "all" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setViewModalOpen(false);
          }}
        >
          <div style={S.viewModal}>
            <div style={S.viewModalHdr}>
              <span style={S.viewModalTitle}>Endpoint Details</span>
              <button
                style={S.drawerClose}
                onClick={() => setViewModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div style={S.viewModalBody}>
              {viewModalLoading ? (
                <div style={{ ...S.drawerLoading, height: 160 }}>
                  Loading...
                </div>
              ) : viewModalData ? (
                <>
                  <div style={S.viewSection}>
                    <div style={S.viewSectionTitle}>Overview</div>
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Endpoint URL</span>
                      <span style={S.viewValMono}>{viewModalData.url}</span>
                    </div>
                    {viewModalData.description && (
                      <div style={S.viewRow}>
                        <span style={S.viewKey}>Description</span>
                        <span style={S.viewVal}>
                          {viewModalData.description}
                        </span>
                      </div>
                    )}
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Status</span>
                      <Badge status={viewModalData.status} />
                    </div>
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Events Mode</span>
                      <span style={S.viewVal}>
                        {viewModalData.eventsMode === "all"
                          ? "All events"
                          : `${viewModalData.eventCount || 0} selected`}
                      </span>
                    </div>
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Last Delivery</span>
                      <span style={S.viewVal}>
                        {viewModalData.lastDelivery || "—"}
                      </span>
                    </div>
                  </div>

                  <div style={S.viewSection}>
                    <div style={S.viewSectionTitle}>Delivery Settings</div>
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Retry on Failure</span>
                      <span
                        style={{
                          ...S.badge,
                          background:
                            viewModalData.retryEnabled === "Y"
                              ? "rgba(16,185,129,.1)"
                              : "rgba(100,116,139,.1)",
                          color:
                            viewModalData.retryEnabled === "Y"
                              ? "#10b981"
                              : "var(--text-color)",
                          fontSize: 11,
                        }}
                      >
                        {viewModalData.retryEnabled === "Y"
                          ? "Enabled"
                          : "Disabled"}
                      </span>
                    </div>
                    <div style={S.viewRow}>
                      <span style={S.viewKey}>Max Retries</span>
                      <span style={S.viewVal}>
                        {viewModalData.maxRetries ?? "—"}
                      </span>
                    </div>
                    <div style={{ ...S.viewRow, border: "none" }}>
                      <span style={S.viewKey}>Timeout</span>
                      <span style={S.viewVal}>
                        {viewModalData.timeoutSec
                          ? `${viewModalData.timeoutSec}s`
                          : "—"}
                      </span>
                    </div>
                  </div>

                  {viewModalData.eventsMode === "select" &&
                    viewModalData.events &&
                    viewModalData.events.length > 0 && (
                      <div style={{ ...S.viewSection, marginBottom: 0 }}>
                        <div style={S.viewSectionTitle}>Selected Events</div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px 6px",
                            marginTop: 10,
                          }}
                        >
                          {viewModalData.events.map((ev) => (
                            <span key={ev} style={S.viewEventPill}>
                              {ev}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  <div style={{ ...S.viewSection, marginBottom: 0 }}>
                    <div style={S.viewSectionTitle}>Security</div>
                    <div style={{ ...S.viewRow, border: "none" }}>
                      <span style={S.viewKey}>Signing Secret</span>
                      <span style={{ ...S.viewValMono, opacity: 0.5 }}>
                        {viewModalData.secret
                          ? viewModalData.secret
                          : "Not set"}
                      </span>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <div style={S.viewModalFooter}>
              <button
                style={{ ...S.btnCancel, height: 36, fontSize: 13 }}
                onClick={() => setViewModalOpen(false)}
              >
                Close
              </button>
              {viewModalData && !isReadOnly && (
                <button
                  style={{
                    ...S.btnSave,
                    height: 36,
                    fontSize: 13,
                    padding: "0 18px",
                  }}
                  onClick={() => {
                    setViewModalOpen(false);
                    openDrawer(viewModalData.epId);
                  }}
                >
                  Edit Endpoint
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Log Detail Modal ── */}
      {logModalOpen && (
        <div
          style={{ ...S.logModalOverlay, pointerEvents: "all" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLogModalOpen(false);
          }}
        >
          <div style={S.logModal}>
            <div style={S.logModalHdr}>
              <span style={S.logModalTitle}>
                {selectedLog
                  ? `Event: ${selectedLog.eventName}`
                  : "Event Details"}
              </span>
              <button
                style={S.drawerClose}
                onClick={() => setLogModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div style={S.logModalBody}>
              {logModalLoading ? (
                <div style={{ ...S.drawerLoading, height: 160 }}>
                  Loading log details...
                </div>
              ) : selectedLog ? (
                <>
                  <div style={S.logSection}>
                    <div style={S.logSectionTitle}>Response</div>
                    <div style={S.respLine}>
                      <span
                        style={{
                          fontWeight: 700,
                          color:
                            selectedLog.respCode < 400 ? "#10b981" : "#ef4444",
                        }}
                      >
                        {selectedLog.respCode}
                      </span>
                      <span
                        style={{ color: "var(--text-color)", opacity: 0.5 }}
                      >
                        {selectedLog.respCode < 400 ? "OK" : "Error"}
                      </span>
                      <span
                        style={{
                          marginLeft: "auto",
                          fontSize: 11,
                          color: "var(--text-color)",
                          opacity: 0.5,
                        }}
                      >
                        {selectedLog.timeDisplay}
                      </span>
                    </div>
                  </div>
                  <div style={S.logSection}>
                    <div style={S.logSectionTitle}>Request Payload</div>
                    <pre style={S.codeBlock}>{selectedLog.payload || "—"}</pre>
                  </div>
                  <div style={S.logSection}>
                    <div style={S.logSectionTitle}>Response Body</div>
                    <pre style={S.codeBlock}>{selectedLog.respBody || "—"}</pre>
                  </div>
                  <div style={S.logSection}>
                    <div style={S.logSectionTitle}>Headers</div>
                    <pre style={S.codeBlock}>{selectedLog.headers || "—"}</pre>
                  </div>
                  {selectedLog.status === "FAILED" && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 16,
                      }}
                    >
                      <button
                        style={{
                          ...S.btnRetry,
                          height: 36,
                          padding: "0 18px",
                          fontSize: 13,
                          borderRadius: 8,
                        }}
                        onClick={() => {
                          retryLog(selectedLog.logId);
                          setLogModalOpen(false);
                        }}
                      >
                        <i
                          className="mdi mdi-refresh"
                          style={{ fontSize: 14 }}
                        />{" "}
                        Retry Delivery
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Dialog ── */}
      {confirmOpen && (
        <div
          style={{ ...S.confirmBg, pointerEvents: "all" }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !confirmLoading)
              setConfirmOpen(false);
          }}
        >
          <div style={S.confirmBox}>
            <div style={S.confirmTitle}>{confirmTitle}</div>
            <div style={S.confirmBody}>{confirmBody}</div>
            <div style={S.confirmBtns}>
              <button
                style={{
                  ...S.btnCancel,
                  height: 36,
                  fontSize: 13,
                  opacity: confirmLoading ? 0.4 : 0.6,
                }}
                onClick={() => setConfirmOpen(false)}
                disabled={confirmLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  ...S.btnDanger,
                  height: 36,
                  padding: "0 16px",
                  fontSize: 13,
                  borderRadius: 8,
                  opacity: confirmLoading ? 0.6 : 1,
                }}
                onClick={() => confirmCb.current && confirmCb.current()}
                disabled={confirmLoading}
              >
                {confirmLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      <div
        style={{
          ...S.toast,
          border: `1px solid ${toastType === "error" ? "#ef4444" : "#10b981"}`,
          transform: toastShow ? "translateY(0)" : "translateY(20px)",
          opacity: toastShow ? 1 : 0,
          pointerEvents: toastShow ? "all" : "none",
        }}
      >
        {toastMsg}
      </div>
    </Fragment>
  );
};

export default Webhooks;